import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  Code2,
  Download,
  Image,
  Monitor,
  Moon,
  Palette,
  PanelLeft,
  Redo2,
  SlidersHorizontal,
  Smartphone,
  Sun,
  Tablet,
  Undo2,
  WandSparkles,
} from 'lucide-react';
import { imagePresets, initialConfig, modeMeta } from './config';
import { LeftInspector } from './components/LeftInspector';
import { PreviewStage } from './components/PreviewStage';
import { RightInspector } from './components/RightInspector';
import { exportStandalone } from './exporter';
import type { DeviceMode, EditorTab, ImagePreset, StudioImages } from './types';
import { useConfigHistory } from './useConfigHistory';

const compactPanelQuery = '(max-width: 68rem)';
const themeStorageKey = 'tactile-studio-theme';
type EditorTheme = 'dark' | 'light';

function getInitialTheme(): EditorTheme {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}

function App() {
  const { config, update, undo, redo, canUndo, canRedo } = useConfigHistory(initialConfig);
  const [images, setImages] = useState<StudioImages>(() => ({ ...imagePresets[0].images }));
  const [activeImagePresetId, setActiveImagePresetId] = useState<string | null>(imagePresets[0].id);
  const [tab, setTab] = useState<EditorTab>('content');
  const [device, setDevice] = useState<DeviceMode>('desktop');
  const [mobilePanel, setMobilePanel] = useState<'left' | 'right' | null>(null);
  const [effectsPanelOpen, setEffectsPanelOpen] = useState(true);
  const [compactPanels, setCompactPanels] = useState(() => window.matchMedia(compactPanelQuery).matches);
  const [theme, setTheme] = useState<EditorTheme>(getInitialTheme);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const media = window.matchMedia(compactPanelQuery);
    const syncPanelMode = () => {
      setCompactPanels(media.matches);
      if (!media.matches) setMobilePanel(null);
    };
    syncPanelMode();
    media.addEventListener('change', syncPanelMode);
    return () => media.removeEventListener('change', syncPanelMode);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      theme === 'light' ? '#f4f7fb' : '#09090b',
    );
    try {
      localStorage.setItem(themeStorageKey, theme);
    } catch {
      // The theme still works for this session when storage is unavailable.
    }
  }, [theme]);

  const selectTab = (next: EditorTab) => {
    setTab(next);
    if (compactPanels) setMobilePanel('left');
  };

  const toggleEffectsPanel = () => {
    if (compactPanels) {
      setMobilePanel((current) => current === 'right' ? null : 'right');
      return;
    }
    setEffectsPanelOpen((current) => !current);
  };

  const handleImage = async (slot: keyof StudioImages, file: File) => {
    if (!file.type.startsWith('image/')) {
      setToast({ kind: 'error', message: '请选择 PNG、JPEG、WebP 或 AVIF 图片。' });
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setToast({ kind: 'error', message: '单张图片请控制在 12MB 以内。' });
      return;
    }
    try {
      const source = await fileToDataUrl(file);
      setImages((current) => ({ ...current, [slot]: source }));
      setActiveImagePresetId(null);
      setToast({ kind: 'success', message: `图片 ${slot.toUpperCase()} 已在本地载入。` });
    } catch {
      setToast({ kind: 'error', message: '图片读取失败，请换一张图片重试。' });
    }
  };

  const handleImagePreset = (preset: ImagePreset) => {
    setImages({ ...preset.images });
    setActiveImagePresetId(preset.id);
    setToast({ kind: 'success', message: `已载入“${preset.name}”预设，可继续调整或导出。` });
    if (compactPanels) setMobilePanel(null);
  };

  const handleSaveImage = (source: string, filename: string, label: string) => {
    const link = document.createElement('a');
    link.href = source;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setToast({ kind: 'success', message: `${label} 已开始保存。` });
  };

  const handleSwapImages = () => {
    setImages((current) => ({ a: current.b, b: current.a }));
    setActiveImagePresetId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportStandalone(config, images);
      setToast({ kind: 'success', message: '独立 HTML 已生成：图片与交互均已内嵌。' });
    } catch (error) {
      setToast({ kind: 'error', message: error instanceof Error ? error.message : '导出失败，请重试。' });
    } finally {
      setExporting(false);
    }
  };

  const activeMode = modeMeta.find((mode) => mode.id === config.mode)!;
  const effectsPanelVisible = compactPanels ? mobilePanel === 'right' : effectsPanelOpen;

  return (
    <div className={`studio-shell ${effectsPanelOpen ? '' : 'effects-panel-collapsed'}`}>
      <header className="global-toolbar">
        <div className="brand-lockup"><span>T</span><strong>TACTILE STUDIO</strong></div>
        <div className="project-breadcrumb"><span>Projects</span><b>›</b><strong>Layers of Time</strong></div>
        <div className="toolbar-actions">
          <button type="button" onClick={undo} disabled={!canUndo} aria-label="撤销"><Undo2 size={17} /></button>
          <button type="button" onClick={redo} disabled={!canRedo} aria-label="重做"><Redo2 size={17} /></button>
          <button
            className="theme-toggle"
            type="button"
            onClick={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
            aria-label={theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
            aria-pressed={theme === 'light'}
            title={theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <span className="offline-status"><i /><span>Offline ready</span></span>
          <button className="mobile-panel-button" type="button" onClick={() => setMobilePanel(mobilePanel === 'left' ? null : 'left')}><PanelLeft size={17} /><span>内容</span></button>
          <button className="mobile-panel-button" type="button" onClick={toggleEffectsPanel} aria-pressed={effectsPanelVisible}><SlidersHorizontal size={17} /><span>效果</span></button>
          <button className="export-button" type="button" disabled={exporting} onClick={handleExport}>
            {exporting ? <WandSparkles size={17} /> : <Download size={17} />}
            <span>{exporting ? '正在封装' : '导出 HTML'}</span>
          </button>
        </div>
      </header>

      <nav className="tool-rail" aria-label="编辑区域">
        <button type="button" className={tab === 'content' ? 'active' : ''} onClick={() => selectTab('content')} aria-label="内容"><PanelLeft size={18} /><span>内容</span></button>
        <button type="button" className={tab === 'media' ? 'active' : ''} onClick={() => selectTab('media')} aria-label="素材"><Image size={18} /><span>素材</span></button>
        <button
          type="button"
          className={effectsPanelVisible ? 'active' : ''}
          onClick={toggleEffectsPanel}
          aria-label="效果"
          aria-controls="effects-inspector"
          aria-pressed={effectsPanelVisible}
          title={effectsPanelVisible ? '收起效果面板，扩大预览' : '展开效果面板'}
        ><WandSparkles size={18} /><span>效果</span></button>
        <button type="button" className={tab === 'style' ? 'active' : ''} onClick={() => selectTab('style')} aria-label="样式"><Palette size={18} /><span>样式</span></button>
        <span className="rail-spacer" />
        <button type="button" onClick={handleExport} aria-label="查看导出"><Code2 size={18} /><span>代码</span></button>
      </nav>

      <div className={`mobile-scrim ${mobilePanel ? 'visible' : ''}`} onClick={() => setMobilePanel(null)} aria-hidden="true" />
      <div className={mobilePanel === 'left' ? 'mobile-panel-open' : ''} data-panel="left">
        <LeftInspector
          tab={tab}
          config={config}
          images={images}
          activeImagePresetId={activeImagePresetId}
          onUpdate={update}
          onImage={handleImage}
          onSelectImagePreset={handleImagePreset}
          onSaveImage={handleSaveImage}
          onSwapImages={handleSwapImages}
          onCloseMobile={() => setMobilePanel(null)}
        />
      </div>

      <section className="preview-workspace" aria-label="实时网页预览">
        <header className="preview-toolbar">
          <div><span>LIVE PREVIEW</span><strong>{activeMode.label}</strong></div>
          <div className="device-switcher" aria-label="预览设备">
            <button type="button" className={device === 'desktop' ? 'active' : ''} onClick={() => setDevice('desktop')} aria-label="桌面预览"><Monitor size={16} /></button>
            <button type="button" className={device === 'tablet' ? 'active' : ''} onClick={() => setDevice('tablet')} aria-label="平板预览"><Tablet size={16} /></button>
            <button type="button" className={device === 'mobile' ? 'active' : ''} onClick={() => setDevice('mobile')} aria-label="手机预览"><Smartphone size={16} /></button>
          </div>
        </header>
        <div className="preview-canvas-area">
          <PreviewStage config={config} images={images} device={device} />
        </div>
        <footer className="preview-statusbar">
          <span><CheckCircle2 size={14} />实时同步</span>
          <span>{device === 'desktop' ? '1440 × 900' : device === 'tablet' ? '768 × 1024' : '390 × 844'}</span>
          <span>100%</span>
        </footer>
      </section>

      <div
        id="effects-inspector"
        className={mobilePanel === 'right' ? 'mobile-panel-open' : ''}
        data-panel="right"
        aria-hidden={!effectsPanelVisible}
      >
        <RightInspector config={config} onUpdate={update} onCloseMobile={() => setMobilePanel(null)} />
      </div>

      {toast && <div className={`toast toast-${toast.kind}`} role="status"><span>{toast.kind === 'success' ? '✓' : '!'}</span>{toast.message}</div>}
    </div>
  );
}

export default App;
